import React, { useState, useEffect } from 'react';
import { IS_PAYMENTS_ENABLED } from '../config';
import WaitlistDialog from '../components/WaitlistDialog';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Check, Crown, Zap, X } from 'lucide-react';
import { toast } from 'sonner';

const features = [
    { name: 'Active Projects', free: '1', pro: 'Unlimited' },
    { name: 'Link Lifespan', free: '7 days', pro: 'Permanent' },
    { name: 'Subtitle Styles', free: 'Standard only', pro: 'All styles' },
    { name: 'Custom Fonts & Colors', free: false, pro: true },
    { name: 'Watermark', free: 'Required', pro: 'Removed' },
    { name: 'Download .SRT', free: false, pro: true },
    { name: 'View Analytics', free: 'Basic', pro: 'Full' },
    { name: 'Priority Support', free: false, pro: true },
];

/* 1. Added 'bonusCaption' to props */
const PricingTier = ({ title, price, period, description, features: tierFeatures, isPro, isPopular, isBestValue, onSelect, userIsPro, crossOutPrice, bonusCaption }) => {

    const getButtonConfig = () => {
        if (isPro) {
            if (userIsPro) return { text: "Manage Subscription", disabled: false };
            return { text: "Lock In Rate", disabled: false };
        } else {
            if (userIsPro) return { text: "Included", disabled: true };
            return { text: "Start Free", disabled: false };
        }
    };

    const btnConfig = getButtonConfig();

    return (
        <div className={`card-base p-8 relative transition-all duration-300 flex flex-col
            ${isPopular ? 'border-primary glow-primary-strong' : ''}
            ${isBestValue ? 'border-[#FF9E70] shadow-[0_0_25px_rgba(255,158,112,0.4)]' : ''} 
        `}>
            {/* LAUNCH SPECIAL BADGE */}
            {isPopular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-heading uppercase tracking-wider px-4 py-1.5 rounded-full">
                        Launch Special
                    </span>
                </div>
            )}

            {/* BEST VALUE BADGE */}
            {isBestValue && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-[#FF9E70] text-black text-xs font-heading uppercase tracking-wider px-4 py-1.5 rounded-full shadow-lg">
                        Best Value
                    </span>
                </div>
            )}

            <div className="text-center mb-8">
                {isPro && <Crown className={`w-8 h-8 mx-auto mb-3 ${isBestValue ? 'text-[#FF9E70]' : 'text-primary'}`} />}
                
                <h3 className="font-heading text-2xl uppercase mb-2">{title}</h3>
                
                {/* PRICE DISPLAY */}
                <div className="flex items-center justify-center gap-2">
                    {crossOutPrice && (
                        <span className="text-muted-foreground text-xl line-through decoration-destructive decoration-2">
                            {crossOutPrice}
                        </span>
                    )}
                    <div className="flex items-baseline gap-1">
                        <span className="font-brand text-5xl">{price}</span>
                        {period && <span className="text-muted-foreground">/{period}</span>}
                    </div>
                </div>

                {/* PRICE LOCK TEXT */}
                {(isPopular || isBestValue) && (
                    <p className={`text-xs mt-2 font-bold uppercase tracking-wide ${isBestValue ? 'text-[#FF9E70]' : 'text-primary'}`}>
                        Price locked while subscribed*
                    </p>
                )}
                
                {(!isPopular && !isBestValue) && <p className="text-sm text-muted-foreground mt-2">{description}</p>}
            </div>

            <ul className="space-y-4 mb-8 flex-grow">
                {tierFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                        {feature.included ? (
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0
                                ${isBestValue ? 'bg-[#FF9E70]/20' : 'bg-primary/20'}`}>
                                <Check className={`w-3 h-3 ${isBestValue ? 'text-[#FF9E70]' : 'text-primary'}`} />
                            </div>
                        ) : (
                            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                                <X className="w-3 h-3 text-muted-foreground" />
                            </div>
                        )}
                        <span className={feature.included ? '' : 'text-muted-foreground'}>
                            {feature.text}
                        </span>
                    </li>
                ))}
            </ul>

            {/* 2. NEW: Bonus Caption Block */}
            {bonusCaption && (
                <div className="mb-6 px-1">
                    <p className={`text-xs text-center italic leading-relaxed ${isBestValue ? 'text-[#FF9E70]/90' : 'text-muted-foreground'}`}>
                        {bonusCaption}
                    </p>
                </div>
            )}

            <Button
                className={`w-full ${
                    isPro 
                        ? (isBestValue 
                            ? 'bg-[#FF9E70] hover:bg-[#FF9E70]/90 text-black font-heading font-bold uppercase tracking-wider' 
                            : 'btn-primary') 
                        : 'btn-secondary'
                }`}
                onClick={onSelect}
                disabled={btnConfig.disabled}
                data-testid={`select-${title.toLowerCase().replace(' ', '-')}-btn`}
            >
                {btnConfig.text}
            </Button>
        </div>
    );
};

export default function Pricing() {
    const navigate = useNavigate();
    const { isAuthenticated, isPro, user } = useAuth();
    const [showWaitlist, setShowWaitlist] = useState(false);

    const handleSelectFree = () => {
        if (isAuthenticated) {
            navigate('/gallery');
        } else {
            navigate('/register');
        }
    };

    const handleSelectPro = async (variant) => {
        if (!isAuthenticated) {
            navigate('/register');
            return;
        }

        const token = localStorage.getItem('subhack_token');

        if (user?.subscription_status === 'pro') {
            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/portal-url`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                // Clone the response to prevent PostHog/Analytics from stealing the body stream
                const responseClone = response.clone();
                
                if (!response.ok) {
                    try {
                        const errorData = await responseClone.json();
                        toast.error(errorData.detail || "Unable to access billing portal.");
                    } catch (e) {
                        toast.error("Unable to access billing portal.");
                    }
                    return;
                }

                const data = await responseClone.json();
                if (data.portal_url) {
                    window.location.href = data.portal_url;
                }
            } catch (error) {
                console.error('Failed to get portal URL:', error);
                toast.error("An error occurred while connecting to billing.");
            }
            return;
        }

        if (!IS_PAYMENTS_ENABLED) {
            setShowWaitlist(true);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/checkout-url?variant=${variant}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.checkout_url) window.location.href = data.checkout_url;
        } catch (error) {
            console.error('Failed to get checkout URL:', error);
        }
    };

    return (
        <Layout>
            <div className="min-h-screen py-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
                            <Zap className="w-4 h-4 text-primary" />
                            <span className="text-sm font-medium text-primary">Limited Time Offer</span>
                        </div>
                        <h1 className="font-brand text-5xl md:text-6xl uppercase mb-4">
                            Founder <span className="text-primary">Pricing</span>
                        </h1>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            Lock in these rates forever. Price increases coming soon.
                        </p>
                    </div>

                    {/* Pricing Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                        <PricingTier
                            title="Free"
                            price="$0"
                            period=""
                            description="Perfect for trying out SubHack"
                            features={[
                                { text: '1 active project', included: true },
                                { text: 'Access featured clips only', included: true },
                                { text: 'Share links expire in 7 days', included: true },
                                { text: 'Standard subtitle style', included: true },
                                { text: 'View count analytics', included: true },
                                { text: 'SubHack watermark', included: true },
                                { text: 'Custom fonts & colors', included: false },
                                { text: 'Download .SRT files', included: false },
                            ]}
                            isPro={false}
                            userIsPro={isPro}
                            onSelect={handleSelectFree}
                        />

                        <PricingTier
                            title="Pro Monthly"
                            price="$5"
                            crossOutPrice="$9.99"
                            period="month"
                            description="The full experience"
                            features={[
                                { text: 'Unlimited projects', included: true },
                                { text: 'Access to ALL Vault clips', included: true },
                                { text: 'Permanent share links', included: true },
                                { text: 'All subtitle styles', included: true },
                                { text: 'View count analytics', included: true },
                                { text: 'No ads', included: true },
                                { text: 'Download .SRT files', included: true },
                            ]}
                            isPro={true}
                            isPopular={true}
                            userIsPro={isPro}
                            onSelect={() => handleSelectPro('monthly')}
                        />

                        {/* 3. Updated Annual Card with 'bonusCaption' */}
                        <PricingTier
                            title="Pro Annual"
                            price="$48"
                            crossOutPrice="$96"
                            period="year"
                            description="Save 20% with annual billing"
                            features={[
                                { text: 'Everything in Pro Monthly', included: true },
                                { text: 'Save 20% each year', included: true },
                                { text: 'Shape the product roadmap', included: true },
                                { text: 'Access to exclusive Vault clips', included: true }
                            ]}
                            isPro={true}
                            isBestValue={true}
                            userIsPro={isPro}
                            onSelect={() => handleSelectPro('annual')}
                            // ADDED HERE:
                            bonusCaption="As a founding annual subscriber, you'll join the inner circle and have a seat at the table. Access the hidden clip vault and have a direct voice in shaping our product roadmap!"
                        />
                    </div>

                    {/* Feature Comparison Table */}
                    <div className="max-w-4xl mx-auto mb-16">
                        <h2 className="font-heading text-2xl uppercase text-center mb-8">
                            Feature Comparison
                        </h2>
                        <div className="card-base overflow-hidden">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-border">
                                        <th className="text-left p-4 font-heading uppercase text-sm">Feature</th>
                                        <th className="text-center p-4 font-heading uppercase text-sm">Free</th>
                                        <th className="text-center p-4 font-heading uppercase text-sm text-primary">Pro</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {features.map((feature, idx) => (
                                        <tr key={idx} className="border-b border-border last:border-0">
                                            <td className="p-4 text-sm">{feature.name}</td>
                                            <td className="p-4 text-center">
                                                {typeof feature.free === 'boolean' ? (
                                                    feature.free ? (
                                                        <Check className="w-5 h-5 text-primary mx-auto" />
                                                    ) : (
                                                        <X className="w-5 h-5 text-muted-foreground mx-auto" />
                                                    )
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">{feature.free}</span>
                                                )}
                                            </td>
                                            <td className="p-4 text-center">
                                                {typeof feature.pro === 'boolean' ? (
                                                    feature.pro ? (
                                                        <Check className="w-5 h-5 text-primary mx-auto" />
                                                    ) : (
                                                        <X className="w-5 h-5 text-muted-foreground mx-auto" />
                                                    )
                                                ) : (
                                                    <span className="text-sm font-medium">{feature.pro}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* FINE PRINT */}
                    <div className="max-w-2xl mx-auto text-center border-t border-border/50 pt-8 mb-16">
                        <p className="text-[12px] text-muted-foreground/60 leading-relaxed">
                            * <strong>Founder Pricing Terms:</strong> The discounted Founder Pricing rate is locked in for the lifetime of your continuous subscription.
                            If you cancel your plan or your subscription lapses due to payment failure, you will forfeit the Founder Pricing rate
                            and will be subject to the current standard pricing upon any subsequent resubscription.
                        </p>
                    </div>

                    {/* FAQ or CTA */}
                    <div className="text-center">
                        <p className="text-muted-foreground mb-4">
                            Questions? Contact us at support@subhack.com
                        </p>
                        {isPro && (
                            <p className="text-primary font-medium">
                                Thank you for being a Pro member!
                            </p>
                        )}
                    </div>
                </div>
            </div>
            <WaitlistDialog 
                open={showWaitlist} 
                onOpenChange={setShowWaitlist} 
                defaultEmail={user?.email} 
            />
        </Layout>
    );
}